import React, { Suspense, useContext } from "react";
import ReactDOMServer from "react-dom/server";
import { ReactRelayContext } from "react-relay";

import { faLink } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Node as ProseNode } from "prosemirror-model";
import { Plugin, PluginKey, Transaction } from "prosemirror-state";
import { Decoration, DecorationSet, EditorView, type NodeView } from "prosemirror-view";
import { el } from "redom";
import { graphql } from "relay-runtime";
import uuid from "uuid";

import ErrorBoundary from "~/common/ErrorBoundary";
import { cloneNode, type DOMComponent } from "~/lib/dom";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { isFirefox, isSafari } from "~/lib/utils";

import { NodeExtension } from "../../extension";
import { reactRender } from "../../plugins/reactHost";
import type { getPosHandler } from "../../types";

import type { linkPillGetLinkInfoQuery } from "~/__generated__/linkPillGetLinkInfoQuery.graphql";

import styles from "./linkPill.module.css";

export class LinkPill extends NodeExtension {
	get name() {
		return "link_pill";
	}
	get node(): NodeExtension["node"] {
		return {
			group: "inline",
			// We keep a content here because:
			// 1. we don't support entities without content
			// 2. if this content doesn't match the url, this content is used as the title of the pill. (Same behavior as regular link mark)
			//    otherwise title is parsed from html, if user is authenticated.
			// 3. when sending notes through email or other integrations, pills will show as regular links and this is the link text
			content: "text{0,1}",
			attrs: {
				entityId: {},
				url: {},
			},
			marks: "",
			inline: true,
			atom: true,
			isolating: true,
			defining: true,
			draggable: false,
			toDOM(node) {
				// this is used when copying
				return ["a", { href: node.attrs.url, "data-fellow-link-pill": "" }, node.textContent];
			},
			parseDOM: [
				{
					tag: "a[data-fellow-link-pill]",
					getAttrs: (dom: Node | string) => {
						if (!(dom instanceof Element)) return false;
						const url = dom.getAttribute("href");
						if (!url) return false;
						return { url, entityId: uuid.v4() };
					},
					priority: 51,
				},
				{
					tag: "a[data-inline-card]", // confluence
					getAttrs: (dom: Node | string) => {
						if (!(dom instanceof Element)) return false;
						if (!dom.textContent) return false;
						const url = dom.getAttribute("href");
						if (!url) return false;
						return { url, entityId: uuid.v4() };
					},
					priority: 51,
				},
			],
		};
	}
	view = (node: ProseNode, view: EditorView, getPos: getPosHandler, decorations: readonly Decoration[]) => {
		return new LinkPillView(node, view, getPos, decorations, {
			forStaticDOM: this.options.forStaticDOM,
			isPublic: this.options.isPublic,
		});
	};
	get plugins() {
		const ext = this;
		const cursorFixerNode = document.createElement("span");
		if (isFirefox || isSafari) {
			cursorFixerNode.classList.add(styles.linkPillCursorFixFirefoxSafari);
		}
		return [
			new Plugin({
				key: new PluginKey("LinkPillCursorFix"),
				props: {
					decorations(state) {
						const {
							selection: { $from, empty },
						} = state;
						if (!empty) {
							return null;
						}
						if ($from.parentOffset === 0 && $from.nodeAfter?.type.name === ext.name) {
							return DecorationSet.create(state.doc, [Decoration.widget($from.pos, cursorFixerNode)]);
						}
						return null;
					},
				},
			}),
		];
	}
}

function LinkPillDisplay({ url, nodeText, isPublic }: { url: string; nodeText: string; isPublic: boolean }) {
	const relayContext = useContext(ReactRelayContext);
	const fallback = <LinkPillDisplayDumb title={url} icon="" url={url} nodeText={nodeText} />;
	if (!relayContext?.environment || isPublic) {
		// not inside relay context, meaning it's public view. Just show the URL as pill title if no title given from node.textContent.
		return fallback;
	}
	return (
		<Suspense fallback={fallback}>
			<ErrorBoundary fallback={fallback}>
				<LinkPillDisplayWithLoader url={url} nodeText={nodeText} />
			</ErrorBoundary>
		</Suspense>
	);
}

function LinkPillDisplayDumb({
	icon,
	title,
	url,
	nodeText,
}: {
	icon?: string;
	title: string;
	url: string;
	nodeText: string;
}) {
	const [iconFailed, setIconFailed] = React.useState(false);
	const [fallbackIconFailed, setFallbackIconFailed] = React.useState(false);
	const fallbackIcon = React.useMemo(() => {
		let value = "";
		try {
			const u = new URL(url);

			u.pathname = "favicon.ico";
			value = u.toString();
		} catch (_e) {}
		return value;
	}, [url]);

	React.useEffect(() => {
		setIconFailed(!icon);
		setFallbackIconFailed(!fallbackIcon);
	}, [icon, fallbackIcon, setIconFailed, setFallbackIconFailed]);

	const markIconAsFailed = React.useCallback(() => {
		setIconFailed(true);
	}, [setIconFailed]);

	const markFallbackIconAsFailed = React.useCallback(() => {
		setFallbackIconFailed(true);
	}, [setFallbackIconFailed]);

	const titleText = nodeText === url ? title || nodeText : nodeText;

	const iconEl = (() => {
		if (icon === undefined) {
			return <FontAwesomeIcon className={styles.linkPillDisplayIcon} icon={faLink} />;
		}
		if (!iconFailed && !fallbackIconFailed) {
			return <img className={styles.linkPillDisplayIcon} src={icon} onError={markIconAsFailed} />;
		} else if (iconFailed && !fallbackIconFailed) {
			return <img className={styles.linkPillDisplayIcon} src={fallbackIcon} onError={markFallbackIconAsFailed} />;
		}
		return <FontAwesomeIcon className={styles.linkPillDisplayIcon} icon={faLink} />;
	})();

	return (
		<span className={styles.linkPillDisplay}>
			{iconEl}
			<span title={titleText} className={styles.linkPillDisplayTitle}>
				{titleText}
			</span>
		</span>
	);
}

function LinkPillDisplayWithLoader({ url, nodeText }: { url: string; nodeText: string }) {
	const data = useLazyLoadQuery<linkPillGetLinkInfoQuery>(
		graphql`
			query linkPillGetLinkInfoQuery($url: String!) {
				getLinkInfo(url: $url) {
					icon
					title
				}
			}
		`,
		{ url },
		{ fetchPolicy: "store-or-network" },
	);
	return (
		<LinkPillDisplayDumb
			icon={data.getLinkInfo?.icon || ""}
			title={data.getLinkInfo?.title || url}
			url={url}
			nodeText={nodeText}
		/>
	);
}

type LinkPillComponentData = {
	node: ProseNode;
	view: EditorView;
	options: { [k: string]: unknown };
	decorations: readonly Decoration[];
	getPos: boolean | (() => number);
};

class LinkPillComponent implements DOMComponent<LinkPillComponentData> {
	el: HTMLElement;
	data?: LinkPillComponentData;
	forStaticDOM?: boolean;
	isPublic?: boolean;
	static template = el(
		"a",
		{
			href: "#",
			pmentity: "",
			class: `${styles.linkPillContainer}`,
			linkPillContainer: "",
			contenteditable: "false",
			"data-link-pill": "",
			target: "_blank",
			rel: "noopener noreferrer",
		},
		el(
			"span",
			{
				style: {
					fontSize: 0,
					display: "inline-block", // this is to disable cursor in this element in safari
				},
			},
			// this is nbsp; chrome breaks without this when deleting the last leading character before this node
			String.fromCharCode(160),
		),
	);
	constructor(
		data: LinkPillComponentData,
		{ forStaticDOM = false, isPublic = false }: { forStaticDOM?: boolean; isPublic?: boolean },
	) {
		this.el = cloneNode(LinkPillComponent.template);
		this.el.setAttribute("portal-id", uuid.v4());
		this.forStaticDOM = forStaticDOM;
		this.isPublic = isPublic;
		this.update(data);
	}
	update(data: LinkPillComponentData): void {
		const url = data.node.attrs.url;
		this.el.setAttribute("href", url || "#");
		if (this.forStaticDOM) {
			this.el.innerHTML = ReactDOMServer.renderToString(
				<LinkPillDisplayDumb title={url} url={url} nodeText={data.node.textContent} />,
			);
		} else {
			if (!url || typeof url !== "string") {
				reactRender(data.view, this.el, true);
			}
			reactRender(
				data.view,
				this.el,
				<LinkPillDisplay url={url} nodeText={data.node.textContent} isPublic={this.isPublic ?? false} />,
				true,
			);
		}
		this.data = data;
	}
	deinit(): void {
		if (this.data?.view) {
			// Don't eagerly flush here.
			// prose sometimes destroy and re-create nodes.
			reactRender(this.data.view, this.el, null);
		}
	}
}

class LinkPillView implements NodeView {
	component: LinkPillComponent;
	dom: HTMLElement;

	constructor(
		public node: ProseNode,
		public view: EditorView,
		public getPos: getPosHandler,
		decorations: readonly Decoration[],
		public options: { [k: string]: unknown },
	) {
		this.options = { ...options };
		this.component = new LinkPillComponent(
			{
				node,
				view,
				getPos,
				decorations,
				options: this.options,
			},
			{
				forStaticDOM: this.options.forStaticDOM as boolean,
				isPublic: this.options.isPublic as boolean,
			},
		);
		this.dom = this.component.el;
	}

	update(newNode: ProseNode, decorations: readonly Decoration[]) {
		if (newNode.type !== this.node.type) {
			return false;
		}
		this.component.update({
			node: newNode,
			view: this.view,
			getPos: this.getPos,
			decorations,
			options: this.options,
		});
		this.node = newNode;
		return true;
	}

	destroy() {
		this.component.deinit();
	}

	ignoreMutation(_record: MutationRecord | { type: "selection"; target: Element }): boolean {
		// since this view is completely rendered by us, we ignroe all mutations inside of it.
		// otherwise prose would try to do it's own thing and destroy the node on first react render.
		return true;
	}
}

export function insertLinkPillTr(tr: Transaction, url: string, from: number, to?: number, text?: string) {
	const schema = tr.doc.type.schema;
	const newNode = schema.nodes["link_pill"].createAndFill({ url: url, entityId: uuid.v4() }, schema.text(text || url));
	if (!newNode) {
		console.warn("Failed to create link pill node", url);
		return tr;
	}
	tr.replaceWith(from, to || from, newNode);
	return tr;
}
